import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Emitter, Event } from 'vscode/vscode/vs/base/common/event';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { LogLevel } from 'vscode/vscode/vs/platform/log/common/log';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { throttle } from 'vscode/vscode/vs/base/common/decorators';

let PromptInputModel = class PromptInputModel extends Disposable {
    get value() { return this._value; }
    get cursorIndex() { return this._cursorIndex; }
    get ghostTextIndex() { return this._ghostTextIndex; }
    constructor(_xterm, onCommandStart, onCommandExecuted, _logService) {
        super();
        this._xterm = _xterm;
        this._logService = _logService;
        this._state = 0 ;
        this._commandStartX = 0;
        this._lastUserInput = '';
        this._value = '';
        this._cursorIndex = 0;
        this._ghostTextIndex = -1;
        this._onDidStartInput = this._register(( new Emitter()));
        this.onDidStartInput = this._onDidStartInput.event;
        this._onDidChangeInput = this._register(( new Emitter()));
        this.onDidChangeInput = this._onDidChangeInput.event;
        this._onDidFinishInput = this._register(( new Emitter()));
        this.onDidFinishInput = this._onDidFinishInput.event;
        this._onDidInterrupt = this._register(( new Emitter()));
        this.onDidInterrupt = this._onDidInterrupt.event;
        this._register(Event.any(this._xterm.onCursorMove, this._xterm.onData, this._xterm.onWriteParsed)(() => this._sync()));
        this._register(this._xterm.onData(e => this._handleUserInput(e)));
        this._register(onCommandStart(e => this._handleCommandStart(e)));
        this._register(onCommandExecuted(() => this._handleCommandExecuted()));
        this._register(this.onDidStartInput(() => this._logCombinedStringIfTrace('PromptInputModel#onDidStartInput')));
        this._register(this.onDidChangeInput(() => this._logCombinedStringIfTrace('PromptInputModel#onDidChangeInput')));
        this._register(this.onDidFinishInput(() => this._logCombinedStringIfTrace('PromptInputModel#onDidFinishInput')));
        this._register(this.onDidInterrupt(() => this._logCombinedStringIfTrace('PromptInputModel#onDidInterrupt')));
    }
    _logCombinedStringIfTrace(message) {
        if (this._logService.getLevel() === LogLevel.Trace) {
            this._logService.trace(message, this.getCombinedString());
        }
    }
    setContinuationPrompt(value) {
        this._continuationPrompt = value;
    }
    setConfidentCommandLine(value) {
        if (this._value !== value) {
            this._value = value;
            this._cursorIndex = -1;
            this._ghostTextIndex = -1;
            this._onDidChangeInput.fire(this._createStateObject());
        }
    }
    getCombinedString() {
        const value = this._value.replaceAll('\n', '\u23CE');
        if (this._cursorIndex === -1) {
            return value;
        }
        let result = `${value.substring(0, this.cursorIndex)}|`;
        if (this.ghostTextIndex !== -1) {
            result += `${value.substring(this.cursorIndex, this.ghostTextIndex)}[`;
            result += `${value.substring(this.ghostTextIndex)}]`;
        }
        else {
            result += value.substring(this.cursorIndex);
        }
        return result;
    }
    _handleCommandStart(command) {
        if (this._state === 1 ) {
            return;
        }
        this._state = 1 ;
        this._commandStartMarker = command.marker;
        this._commandStartX = this._xterm.buffer.active.cursorX;
        this._value = '';
        this._cursorIndex = 0;
        this._onDidStartInput.fire(this._createStateObject());
        this._onDidChangeInput.fire(this._createStateObject());
    }
    _handleCommandExecuted() {
        if (this._state === 2 ) {
            return;
        }
        this._cursorIndex = -1;
        if (this._ghostTextIndex !== -1) {
            this._value = this._value.substring(0, this._ghostTextIndex);
            this._ghostTextIndex = -1;
        }
        const event = this._createStateObject();
        if (this._lastUserInput === '\u0003') {
            this._onDidInterrupt.fire(event);
        }
        this._state = 2 ;
        this._onDidFinishInput.fire(event);
        this._onDidChangeInput.fire(event);
    }
    _sync() {
        if (this._state !== 1 ) {
            return;
        }
        const commandStartY = this._commandStartMarker?.line;
        if (commandStartY === undefined) {
            return;
        }
        const buffer = this._xterm.buffer.active;
        let line = buffer.getLine(commandStartY);
        const commandLine = line?.translateToString(true, this._commandStartX);
        if (!line || commandLine === undefined) {
            this._logService.trace(`PromptInputModel#_sync: no line`);
            return;
        }
        const absoluteCursorY = buffer.baseY + buffer.cursorY;
        let value = commandLine;
        let cursorIndex = absoluteCursorY === commandStartY ? this._getRelativeCursorIndex(this._commandStartX, buffer, line) : commandLine.length + 1;
        let ghostTextIndex = -1;
        if (absoluteCursorY === commandStartY && buffer.cursorX > 1) {
            ghostTextIndex = this._scanForGhostText(buffer, line, cursorIndex);
        }
        for (let y = commandStartY + 1; y <= absoluteCursorY; y++) {
            line = buffer.getLine(y);
            let lineText = line?.translateToString(true);
            if (lineText && line) {
                if (this._continuationPrompt === undefined || this._lineContainsContinuationPrompt(lineText)) {
                    lineText = this._trimContinuationPrompt(lineText);
                    value += `\n${lineText}`;
                    cursorIndex += (absoluteCursorY === y
                        ? this._getRelativeCursorIndex(this._getContinuationPromptCellWidth(line, lineText), buffer, line)
                        : lineText.length + 1);
                }
                else {
                    break;
                }
            }
        }
        for (let y = absoluteCursorY + 1; y < buffer.baseY + this._xterm.rows; y++) {
            line = buffer.getLine(y);
            const lineText = line?.translateToString(true);
            if (lineText && line) {
                if (this._continuationPrompt === undefined || this._lineContainsContinuationPrompt(lineText)) {
                    value += `\n${this._trimContinuationPrompt(lineText)}`;
                }
                else {
                    break;
                }
            }
            else {
                break;
            }
        }
        if (this._logService.getLevel() === LogLevel.Trace) {
            this._logService.trace(`PromptInputModel#_sync: ${this.getCombinedString()}`);
        }
        if (this._value !== value || this._cursorIndex !== cursorIndex || this._ghostTextIndex !== ghostTextIndex) {
            this._value = value;
            this._cursorIndex = cursorIndex;
            this._ghostTextIndex = ghostTextIndex;
            this._onDidChangeInput.fire(this._createStateObject());
        }
    }
    _handleUserInput(e) {
        this._lastUserInput = e;
    }
    _scanForGhostText(buffer, line, cursorIndex) {
        let ghostTextIndex = -1;
        let proceedWithGhostTextCheck = false;
        let x = buffer.cursorX;
        while (x > 0) {
            const cell = line.getCell(--x);
            if (!cell) {
                break;
            }
            if (cell.getChars().trim().length > 0) {
                proceedWithGhostTextCheck = !this._isCellStyledLikeGhostText(cell);
                break;
            }
        }
        if (proceedWithGhostTextCheck) {
            let potentialGhostIndexOffset = 0;
            let x = buffer.cursorX;
            while (x < line.length) {
                const cell = line.getCell(x++);
                if (!cell || cell.getCode() === 0) {
                    break;
                }
                if (this._isCellStyledLikeGhostText(cell)) {
                    ghostTextIndex = cursorIndex + potentialGhostIndexOffset;
                    break;
                }
                potentialGhostIndexOffset += cell.getChars().length;
            }
        }
        return ghostTextIndex;
    }
    _trimContinuationPrompt(lineText) {
        if (this._lineContainsContinuationPrompt(lineText)) {
            lineText = lineText.substring(this._continuationPrompt.length);
        }
        return lineText;
    }
    _lineContainsContinuationPrompt(lineText) {
        return !!(this._continuationPrompt && lineText.startsWith(this._continuationPrompt));
    }
    _getContinuationPromptCellWidth(line, lineText) {
        if (!this._continuationPrompt || !lineText.startsWith(this._continuationPrompt)) {
            return 0;
        }
        let buffer = '';
        let x = 0;
        while (buffer !== this._continuationPrompt) {
            buffer += line.getCell(x++).getChars();
        }
        return x;
    }
    _getRelativeCursorIndex(startCellX, buffer, line) {
        return line?.translateToString(true, startCellX, buffer.cursorX).length ?? 0;
    }
    _isCellStyledLikeGhostText(cell) {
        return !!(cell.isItalic() || cell.isDim());
    }
    _createStateObject() {
        return ( Object.freeze({
            value: this._value,
            cursorIndex: this._cursorIndex,
            ghostTextIndex: this._ghostTextIndex
        }));
    }
};
PromptInputModel.__decorator = ( __decorate([
    throttle(0)
], PromptInputModel.prototype, "_sync", null));
PromptInputModel = ( __decorate([
    ( __param(3, ILogService))
], PromptInputModel));

export { PromptInputModel };
